import React, { useState } from 'react';
import APP_COLOURS from '../../config/colors';
import { managerAPI } from '../../config/api';

const NewStaffForm = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    cell: '',
    gender: '',
    dob: '',
    id_passport: '',
    position: '',
    employee_id: '',
    debtorIdType: '', // '1' = Passport, '2' = RSA ID, '3' = Temp Residence
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error when user makes changes
  };

  const validateForm = () => {
    const required = ['first_name', 'last_name', 'email', 'cell', 'gender', 'dob', 'id_passport', 'position'];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      setError(`Please fill in all required fields: ${missing.join(', ')}`);
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Cell number validation (basic)
    if (formData.cell.length < 10) {
      setError('Please enter a valid cell number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await managerAPI.createStaff(formData);
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating staff:', err);
      setError(err.message || 'Failed to create staff member');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.first_name && 
           formData.last_name && 
           formData.email && 
           formData.cell && 
           formData.gender && 
           formData.dob && 
           formData.id_passport && 
           formData.position;
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ backgroundColor: APP_COLOURS.BG2 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 px-6 py-4 border-b"
          style={{ 
            backgroundColor: APP_COLOURS.BG2,
            borderColor: `${APP_COLOURS.BLUE}30`
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              Add New Staff Member
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors duration-200"
              style={{ 
                color: APP_COLOURS.PLACEHOLDER,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
                e.currentTarget.style.color = APP_COLOURS.TEXTCOLOR;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = APP_COLOURS.PLACEHOLDER;
              }}
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div 
              className="p-4 rounded-lg border"
              style={{ 
                backgroundColor: 'rgba(255, 95, 95, 0.1)',
                borderColor: APP_COLOURS.ERROR
              }}
            >
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle mr-2" style={{ color: APP_COLOURS.ERROR }}></i>
                <p className="text-sm" style={{ color: APP_COLOURS.ERROR }}>{error}</p>
              </div>
            </div>
          )}

          {/* Personal Information */}
          <div>
            <h3 className="text-sm font-semibold mb-4 uppercase tracking-wider" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  First Name <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder="Enter first name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Last Name <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder="Enter last name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Email <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder="email@example.com"
                />
              </div>

              {/* Cell Number */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Cell Number <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="tel"
                  value={formData.cell}
                  onChange={(e) => handleInputChange('cell', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder="0712345678"
                />
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Date of Birth <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Gender <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleInputChange('gender', 'male')}
                    className="flex-1 px-4 py-2 rounded-lg border transition-all duration-200"
                    style={{
                      backgroundColor: formData.gender === 'male' ? APP_COLOURS.BLUE : APP_COLOURS.BG3,
                      borderColor: formData.gender === 'male' ? APP_COLOURS.BLUE : `${APP_COLOURS.BLUE}30`,
                      color: formData.gender === 'male' ? 'white' : APP_COLOURS.TEXTCOLOR,
                    }}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('gender', 'female')}
                    className="flex-1 px-4 py-2 rounded-lg border transition-all duration-200"
                    style={{
                      backgroundColor: formData.gender === 'female' ? APP_COLOURS.BLUE : APP_COLOURS.BG3,
                      borderColor: formData.gender === 'female' ? APP_COLOURS.BLUE : `${APP_COLOURS.BLUE}30`,
                      color: formData.gender === 'female' ? 'white' : APP_COLOURS.TEXTCOLOR,
                    }}
                  >
                    Female
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ID Information */}
          <div>
            <h3 className="text-sm font-semibold mb-4 uppercase tracking-wider" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              Identification
            </h3>
            
            {/* ID Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                ID Type <span style={{ color: APP_COLOURS.ERROR }}>*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => handleInputChange('debtorIdType', '2')}
                  className="px-4 py-2 rounded-lg border transition-all duration-200"
                  style={{
                    backgroundColor: formData.debtorIdType === '2' ? APP_COLOURS.BLUE : APP_COLOURS.BG3,
                    borderColor: formData.debtorIdType === '2' ? APP_COLOURS.BLUE : `${APP_COLOURS.BLUE}30`,
                    color: formData.debtorIdType === '2' ? 'white' : APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  RSA ID
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('debtorIdType', '1')}
                  className="px-4 py-2 rounded-lg border transition-all duration-200"
                  style={{
                    backgroundColor: formData.debtorIdType === '1' ? APP_COLOURS.BLUE : APP_COLOURS.BG3,
                    borderColor: formData.debtorIdType === '1' ? APP_COLOURS.BLUE : `${APP_COLOURS.BLUE}30`,
                    color: formData.debtorIdType === '1' ? 'white' : APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  Passport
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('debtorIdType', '3')}
                  className="px-4 py-2 rounded-lg border transition-all duration-200 text-sm"
                  style={{
                    backgroundColor: formData.debtorIdType === '3' ? APP_COLOURS.BLUE : APP_COLOURS.BG3,
                    borderColor: formData.debtorIdType === '3' ? APP_COLOURS.BLUE : `${APP_COLOURS.BLUE}30`,
                    color: formData.debtorIdType === '3' ? 'white' : APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  Temp Residence
                </button>
              </div>
            </div>

            {/* ID Number */}
            {formData.debtorIdType && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {formData.debtorIdType === '1' ? 'Passport Number' : 
                   formData.debtorIdType === '2' ? 'ID Number' : 
                   'Temporary Residence ID'} <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.id_passport}
                  onChange={(e) => handleInputChange('id_passport', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder={formData.debtorIdType === '2' ? '0000000000000' : 'Enter ID number'}
                />
              </div>
            )}
          </div>

          {/* Work Information */}
          <div>
            <h3 className="text-sm font-semibold mb-4 uppercase tracking-wider" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              Work Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Position */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Position <span style={{ color: APP_COLOURS.ERROR }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder="e.g., Sales Associate"
                />
              </div>

              {/* Employee ID */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Employee ID <span className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.employee_id}
                  onChange={(e) => handleInputChange('employee_id', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    borderColor: `${APP_COLOURS.BLUE}30`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                  placeholder="e.g., EMP001"
                />
              </div>
            </div>
          </div>

          {/* Required Fields Notice */}
          <div 
            className="p-4 rounded-lg border"
            style={{ 
              backgroundColor: `${APP_COLOURS.BLUE}10`,
              borderColor: `${APP_COLOURS.BLUE}30`
            }}
          >
            <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              <i className="fas fa-info-circle mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
              Fields marked with <span style={{ color: APP_COLOURS.ERROR }}>*</span> are required
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-lg font-medium transition-all duration-200"
              style={{
                backgroundColor: APP_COLOURS.BG4,
                color: APP_COLOURS.PLACEHOLDER,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = APP_COLOURS.BG5;
                  e.currentTarget.style.color = APP_COLOURS.TEXTCOLOR;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
                  e.currentTarget.style.color = APP_COLOURS.PLACEHOLDER;
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="flex-1 px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center"
              style={{
                backgroundColor: isFormValid() && !loading ? APP_COLOURS.BLUE : APP_COLOURS.BG5,
                color: 'white',
                opacity: loading || !isFormValid() ? 0.6 : 1,
                cursor: loading || !isFormValid() ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus mr-2"></i>
                  Add Staff Member
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewStaffForm;
